import React, { useContext, useEffect, useRef, useState } from "react";
import { CuratedItem } from "../utils/types";
import Spacer from "./Spacer";
import { useTranslation } from "react-i18next";
import styles from "../styles/GalleryItem.module.scss";
import clsx from "clsx";
import Link from "next/link";
import { GalleryCardItem } from "./GalleryCardItem";
import { useWindowSize } from "../utils/hooks";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { formatItems } from "../utils/gallery";
import { MediaQueryContext } from "../context/mediaQuery/context";

interface Props {
  data: CuratedItem;
}

const GalleryItem: React.FC<Props> = ({ data }) => {
  const {
    documentId,
    symbol,
    name,
    xNFTS = [],
    quantity,
    royaltiesEarned,
    floorPrice,
  } = data;

  const { t } = useTranslation();
  const {
    state: { tablet },
  } = useContext(MediaQueryContext);

  const windowSize = useWindowSize();
  const containerRef = useRef<HTMLDivElement>(null);

  const [slidesPerView, setSlidesPerView] = useState(1.3);

  useEffect(() => {
    setSlidesPerView(
      containerRef.current ? (containerRef.current.clientWidth - 24) / 267 : 1.3
    );
  }, [windowSize.width]);

  return (
    <>
      <Spacer height={80} className={"hideOnTablet"} />
      <Spacer height={40} className={"hideOnDesktop"} />
      <div
        className={clsx("rowDesktopColumnTablet ai-end", styles.container)}
        ref={containerRef}
      >
        <div
          className={clsx("columnDesktopRowTablet jc-end ai-start", {
            "w-full": tablet,
          })}
        >
          <div className={"column ai-start"}>
            <div className={styles.collection}>{symbol || name}</div>
            <Spacer height={8} />
            <div className={styles.items}>
              {formatItems(quantity)}{" "}
              {t("galleryAll.item", { count: quantity })}
            </div>
            <Spacer height={24} className={"hideOnTablet"} />
            <Spacer height={16} className={"hideOnDesktop"} />
            <Link href={`/gallery/${documentId}`}>
              <a className={"outlineButton"}>{t("gallery.viewAll")}</a>
            </Link>
          </div>
          <Spacer height={100} className={"hideOnTablet"} />
          <Spacer flex={1} className={"hideOnDesktop"} />
          <div>
            <div className={clsx(styles.info, "row ai-center")}>
              <span>{t("gallery.floorPrice")}:</span>
              <Spacer width={16} />
              <span>{floorPrice > 0 ? `${floorPrice} ETH` : "---"}</span>
            </div>
            <Spacer height={8} />
            <div className={clsx(styles.info, "row ai-center")}>
              <span>{t("gallery.royaltiesEarned")}:</span>
              <Spacer width={16} />
              <span>
                {royaltiesEarned > 0 ? `${royaltiesEarned} ETH` : "---"}
              </span>
            </div>
          </div>
        </div>
        <Spacer flex={1} className={"hideOnTablet"} />
        <Spacer height={24} className={"hideOnDesktop"} />
        {tablet ? (
          <Swiper
            spaceBetween={40}
            slidesPerView={slidesPerView}
            draggable
            allowTouchMove
            className={styles.swiper}
            slidesOffsetAfter={100}
            threshold={12}
          >
            {xNFTS.map((item) => {
              return (
                <SwiperSlide key={`swiper_${item.tokenId}`}>
                  <GalleryCardItem
                    item={item}
                    width={162}
                    containerClassName={styles.galleryCardItem}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        ) : (
          <div className={"row ai-center"}>
            {xNFTS.map((item, index) => {
              return (
                <React.Fragment key={item.tokenId}>
                  <GalleryCardItem
                    item={item}
                    width={162}
                    containerClassName={styles.galleryCardItem}
                  />
                  {index !== xNFTS.length - 1 && <Spacer width={40} />}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export { GalleryItem };
